import React, { useState, useEffect } from 'react';
import { Moon, Sun, Menu, X, Shield } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (darkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Testimonials', href: '/testimonials' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActivePage = (href: string) => {
    return location.pathname === href;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-gray-900' : 'bg-white'}`}>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg"
        title="Open menu"
      >
        <Menu className={`h-6 w-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
      </button>

      {/* Sidebar Navigation */}
      <div className={`fixed left-0 top-0 h-full w-64 z-50 transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r shadow-lg`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center space-x-3">
              <Shield className={`h-8 w-8 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
              <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Rust-Squad.com
              </span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden"
              title="Close menu"
            >
              <X className={`h-6 w-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>
          
          <nav className="space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isActivePage(item.href)
                    ? darkMode
                      ? 'bg-orange-600 text-white'
                      : 'bg-orange-50 text-orange-600'
                    : darkMode
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content - Starts at sidebar edge and spans to right edge */}
      {/* Content is properly contained and centered within this area */}
      {/* Using calc to ensure exact width from sidebar (16rem/64) to right edge */}
      <div className="lg:ml-64 lg:w-[calc(100%-16rem)] w-full min-w-0 overflow-x-hidden">
        {/* Page Content */}
        {children}

        {/* Footer - Stays within main content area to avoid duplication */}
        {/* The footer is positioned to the right of the sidebar, matching the main content flow */}
        <footer className={`py-12 px-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border-t`}>
          {/* Inner container to center and constrain the footer content width */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <Link to="/" className="flex items-center space-x-3 mb-4">
                  <Shield className={`h-8 w-8 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Rust-Squad.com
                  </span>
                </Link>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Your trusted partner for comprehensive rust protection and maintenance solutions.
                </p>
              </div>
              
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Quick Links
                </h4>
                <div className="space-y-2">
                  <Link to="/about" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    About Us
                  </Link>
                  <Link to="/services" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Services
                  </Link>
                  <Link to="/gallery" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Gallery
                  </Link>
                  <Link to="/contact" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Contact
                  </Link>
                </div>
              </div>
              
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Legal
                </h4>
                <div className="space-y-2">
                  <Link to="/privacy-policy" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Privacy Policy
                  </Link>
                  <Link to="/terms-of-service" className={`block ${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Terms & Conditions
                  </Link>
                </div>
              </div>
              
              <div>
                <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Follow Us
                </h4>
                <div className="flex space-x-4">
                  <a href="#" className={`${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Facebook
                  </a>
                  <a href="#" className={`${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    LinkedIn
                  </a>
                  <a href="#" className={`${darkMode ? 'text-gray-300 hover:text-orange-400' : 'text-gray-600 hover:text-orange-600'} transition-colors duration-200`}>
                    Instagram
                  </a>
                </div>
              </div>
            </div>
            
            <div className={`pt-8 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} text-center space-y-2`}>
              {/* Website for sale notice at the top */}
              <p className={`${darkMode ? 'text-orange-400' : 'text-orange-600'} font-medium mb-4`}>
                This website is for sale. Interested? You can add in or adapt it the way you want it. Contact us at{' '}
                <a 
                  href="mailto:aideveloper@appsshowroom.com" 
                  className="underline hover:no-underline"
                >
                  aideveloper@appsshowroom.com
                </a>
              </p>
              {/* Copyright information */}
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                © 2025 Rust-Squad.com. All rights reserved.
              </p>
              {/* Attribution with SEO-friendly link */}
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Designed by{' '}
                <a 
                  href="https://appsshowroom.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`${darkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'} underline hover:no-underline transition-colors`}
                >
                  appsshowroom.com
                </a>
              </p>
              {/* Photo credits */}
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Photos courtesy of Pexels and Canva
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Layout;
